import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { BarChart3, Users, TrendingUp, TrendingDown, Clock, ChevronDown, ChevronUp, ArrowUpDown, Activity, Shield, AlertTriangle, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import AudienceBehaviorSection from './AudienceBehaviorSection';
import AudienceReachCard from './AudienceReachCard';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

// MODE_LABELS is now built per-render inside the component (so titles
// follow the current language). The static defaults below are only
// used if the language context is unavailable for some reason.
const MODE_LABELS_FALLBACK = {
  audience: {
    title: 'Στατιστικά Ακροατών',
    peak: 'Peak', avg: 'Avg', min: 'Min', tlh: 'TLH',
    unit: 'ακροατές', icon: Users,
  },
  activity: {
    title: 'Stream Analytics',
    peak: 'Peak Activity', avg: 'Avg Activity', min: 'Min Activity', tlh: 'Engagement Load',
    unit: 'activity', icon: Activity,
  }
};

// Daypart hour ranges (hoisted - no need to redefine each render)
const DAYPART_HOURS = {
  all: null,
  morning: new Set([6,7,8,9]),
  midday: new Set([10,11,12,13,14]),
  afternoon: new Set([15,16,17,18]),
  drive: new Set([18,19,20,21]),
  night: new Set([22,23,0,1,2,3,4,5]),
};

const SongsAnalyzerListenerReport = ({ stationId, stationName, userId, token }) => {
  const { t } = useLanguage();
  // Build MODE_LABELS per-render so titles/units follow the active language.
  const MODE_LABELS = {
    audience: {
      title: t('i18nB3_listener_stats'),
      peak: 'Peak', avg: 'Avg', min: 'Min', tlh: 'TLH',
      unit: t('i18nB3_listeners'), icon: Users,
    },
    activity: {
      title: 'Stream Analytics',
      peak: 'Peak Activity', avg: 'Avg Activity', min: 'Min Activity', tlh: 'Engagement Load',
      unit: 'activity', icon: Activity,
    }
  };
  const [data, setData] = useState(null);
  // v2.9.159 — Live24 stacked overlay data. When the station has a
  // `live24.gr/stats/highcharts/<sid>` URL configured, this holds the
  // pre-aggregated hourly stats from Live24's own analytics. The chart
  // stacks our internal Avg with Live24 Avg so the user can see both
  // sources contributing to the total. Null = no Live24 URL configured.
  const [live24, setLive24] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [viewMode, setViewMode] = useState('audience');
  const [daypart, setDaypart] = useState('all');
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => {
    const d = new Date();
    return d.toISOString().split('T')[0];
  });
  const [sortKey, setSortKey] = useState('hour');
  const [sortDir, setSortDir] = useState('asc');
  // Active vertical section: 'numbers' | 'audience'
  // (NOTE: 'performance' was moved to a top-level tab — see SongsAnalyzerPage.jsx
  // "Music Performance" tab. Keeps this panel scoped to audience-only metrics.)
  const [section, setSection] = useState('numbers');

  const labels = MODE_LABELS[viewMode];

  // Filter hourly data by daypart (client-side, no API change)
  const filteredHourly = useMemo(() => {
    if (!data?.hourly) return [];
    const hours = DAYPART_HOURS[daypart];
    if (!hours) return data.hourly;
    return data.hourly.filter(h => hours.has(h.hour));
  }, [data?.hourly, daypart]);

  // Recalculate summary from filtered hours
  const filteredSummary = useMemo(() => {
    if (daypart === 'all' && data?.daily_summary) return data.daily_summary;
    const withData = filteredHourly.filter(h => h.samples > 0);
    if (withData.length === 0) return null;
    const peak = Math.max(...withData.map(h => h.peak));
    const min = Math.min(...withData.map(h => h.min));
    const avg = Math.round(withData.reduce((s, h) => s + h.avg, 0) / withData.length);
    const tlh = withData.reduce((s, h) => s + (h.tlh || 0), 0);
    return { peak, min, avg, tlh };
  }, [filteredHourly, daypart, data?.daily_summary]);

  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };

  const fetchReport = useCallback(async () => {
    if (!stationId || !userId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        station_id: stationId,
        user_id: userId,
        date_from: dateFrom,
        date_to: dateTo
      });
      const endpoint = viewMode === 'activity'
        ? `${API}/api/songs-analyzer/activity-report`
        : `${API}/api/songs-analyzer/listener-report`;
      const res = await fetch(`${endpoint}?${params}`, { headers });
      if (res.ok) {
        const json = await res.json();
        setData(json);
      }
    } catch (e) {
      console.error('Error fetching report:', e);
    } finally {
      setLoading(false);
    }
  }, [stationId, userId, dateFrom, dateTo, token, viewMode]);

  // v2.9.159 — Fetch Live24 stacked overlay alongside the main report.
  // 404 = no Live24 URL configured → silently leave live24 as null so
  // the chart falls back to single-source bars. Any other error is
  // logged but non-fatal (we don't want to block the main view).
  const fetchLive24 = useCallback(async () => {
    if (!stationId || viewMode !== 'audience') {
      setLive24(null);
      return;
    }
    try {
      const res = await fetch(
        `${API}/api/admin/listeners/live24-history/${stationId}?date=${dateFrom}`,
        { headers }
      );
      if (res.status === 404) {
        setLive24(null);
        return;
      }
      if (res.ok) {
        const json = await res.json();
        setLive24(json);
      }
    } catch (e) {
      // Non-fatal — the main chart still renders without the overlay
      console.warn('Live24 overlay fetch failed (non-fatal):', e);
      setLive24(null);
    }
  }, [stationId, dateFrom, viewMode, token]);


  useEffect(() => {
    if (expanded) {
      fetchReport();
      fetchLive24();
    }
  }, [expanded, fetchReport, fetchLive24]);

  const sortedHourly = filteredHourly.length > 0
    ? [...filteredHourly].sort((a, b) => {
        const aVal = a[sortKey] ?? 0;
        const bVal = b[sortKey] ?? 0;
        return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
      })
    : [];

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir(key === 'hour' ? 'asc' : 'desc');
    }
  };

  const formatHour = (h) => `${String(h).padStart(2, '0')}:00`;
  const formatNum = (n) => n != null ? n.toLocaleString('el-GR') : '—';
  const formatTLH = (n) => n != null ? n.toLocaleString('el-GR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';

  const chartData = useMemo(() => {
    if (!filteredHourly.length) return [];
    // v2.9.159 — Build a hour-indexed map of Live24 buckets so we can
    // overlay them by exact hour onto the main chart. Default to 0
    // when no Live24 sample exists for an hour.
    const live24ByHour = {};
    if (live24?.hourly) {
      for (const h of live24.hourly) {
        if (h.samples > 0) {
          live24ByHour[h.hour] = h;
        }
      }
    }
    return [...filteredHourly]
      .sort((a, b) => a.hour - b.hour)
      .map(row => {
        const l24 = live24ByHour[row.hour];
        return {
          name: `${String(row.hour).padStart(2, '0')}:00`,
          Peak: row.samples > 0 ? row.peak : 0,
          Avg: row.samples > 0 ? row.avg : 0,
          Min: row.samples > 0 ? row.min : 0,
          TLH: row.samples > 0 ? row.tlh : 0,
          // Live24 stacked overlay — ANTI-DOUBLE-COUNT note: this is the
          // listener count REPORTED BY LIVE24 for the same hour. If your
          // configured listeners_aggregation_mode = "sum", the total in
          // the stacked bar represents the unified audience across both
          // sources. With mode = "max", the overlay is informational —
          // visually showing what Live24 reports vs what our pipeline
          // captured.
          Live24_Avg: l24 ? l24.avg : 0,
          hasData: row.samples > 0,
          hasLive24: !!l24,
        };
      });
  }, [filteredHourly, live24]);

  const hasLive24Overlay = useMemo(
    () => chartData.some(d => d.hasLive24),
    [chartData]
  );

  const hasChartData = chartData.some(d => d.hasData);
  const summary = filteredSummary;

  const ChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const labelMap = viewMode === 'activity'
      ? { Peak: 'Peak Activity', Avg: 'Avg Activity', Min: 'Min Activity', TLH: 'Engagement Load' }
      : {};
    return (
      <div className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-xs shadow-xl">
        <div className="font-semibold text-gray-200 mb-1">{label}</div>
        {payload.map((p, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
            <span className="text-gray-400">{labelMap[p.name] || p.name}:</span>
            <span className="text-gray-100 font-medium">
              {p.name === 'TLH' ? p.value?.toFixed(2) : p.value?.toLocaleString('el-GR')}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const legendFormatter = (value) => {
    if (viewMode !== 'activity') return value;
    const map = { Peak: 'Peak Activity', Avg: 'Avg Activity', Min: 'Min Activity', TLH: 'Engagement Load' };
    return map[value] || value;
  };

  const SortHeader = ({ label, field }) => (
    <th
      className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer select-none hover:text-blue-400 transition-colors"
      onClick={() => handleSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sortKey === field && (
          <span className="text-blue-400">{sortDir === 'asc' ? '↑' : '↓'}</span>
        )}
      </span>
    </th>
  );

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden mt-4" data-testid="sa-listener-report">
      {/* Collapsible Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5 transition-colors"
        data-testid="sa-listener-report-toggle"
      >
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-1.5 font-medium text-white">
            {viewMode === 'activity'
              ? <Activity className="w-4 h-4 text-emerald-400" />
              : <Users className="w-4 h-4 text-blue-400" />
            }
            <span>{labels.title}</span>
          </div>
          {summary && !loading && (
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1 text-rose-400">
                <TrendingUp className="w-3.5 h-3.5" />
                <span className="font-medium">{formatNum(summary.peak)}</span>
                <span className="text-xs text-white/40">{labels.peak.toLowerCase()}</span>
              </span>
              <span className="flex items-center gap-1 text-blue-400">
                <Users className="w-3.5 h-3.5" />
                <span className="font-medium">{formatNum(summary.avg)}</span>
                <span className="text-xs text-white/40">{viewMode === 'activity' ? 'avg' : t('i18nB3_avg_short')}</span>
              </span>
              <span className="hidden sm:flex items-center gap-1 text-emerald-400">
                <Clock className="w-3.5 h-3.5" />
                <span className="font-medium">{formatTLH(summary.tlh)}</span>
                <span className="text-xs text-white/40">{viewMode === 'activity' ? 'load' : 'TLH'}</span>
              </span>
            </div>
          )}
        </div>
        <div className="text-white/40">
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </button>

      {/* Expanded Content */}
      {expanded && (
        <div className="border-t border-white/10">
          <div className="flex flex-col md:flex-row">
            {/* Left vertical tabs (mobile → horizontal pills on top) */}
            <nav
              className="md:w-44 md:shrink-0 md:border-r border-white/10 bg-white/[0.02] flex md:flex-col overflow-x-auto md:overflow-x-visible"
              data-testid="sa-section-tabs"
            >
              {[
                { key: 'numbers',     label: t('i18nB3_numbers'),        Icon: BarChart3, testId: 'sa-tab-numbers' },
                { key: 'audience',    label: t('i18nB3_your_audience'),   Icon: Heart,     testId: 'sa-tab-audience' },
              ].map(({ key, label, Icon, testId }) => {
                const active = section === key;
                return (
                  <button
                    key={key}
                    data-testid={testId}
                    onClick={() => setSection(key)}
                    className={`flex items-center gap-2 px-4 py-3 text-sm transition-colors whitespace-nowrap md:whitespace-normal md:border-l-2 border-b-2 md:border-b-0 ${
                      active
                        ? 'bg-blue-500/10 text-blue-300 md:border-l-blue-400 border-b-blue-400'
                        : 'text-white/60 hover:text-white hover:bg-white/5 md:border-l-transparent border-b-transparent'
                    }`}
                  >
                    <Icon className="w-4 h-4 shrink-0" />
                    <span>{label}</span>
                  </button>
                );
              })}
            </nav>

            {/* Section content */}
            <div className="flex-1 p-4 space-y-4">
              {section === 'audience' && (
                <>
                  <AudienceReachCard
                    stationId={stationId}
                    token={token}
                    date={dateFrom}
                    variant="dark"
                    userFacing={true}
                  />
                  <AudienceBehaviorSection
                    stationId={stationId}
                    token={token}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    daypart={daypart}
                  />
                </>
              )}
              {section === 'numbers' && (
                <>
          {/* Mode Toggle */}
          <div className="flex items-center gap-3" data-testid="sa-mode-toggle-container">
            <div className="flex items-center gap-0.5 bg-white/10 rounded-lg p-0.5 border border-white/10" data-testid="sa-mode-toggle">
              <button
                onClick={() => setViewMode('audience')}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                  viewMode === 'audience'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-white/40 hover:text-white/70'
                }`}
                data-testid="sa-mode-toggle-audience"
              >
                Live Audience
              </button>
              <button
                onClick={() => setViewMode('activity')}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                  viewMode === 'activity'
                    ? 'bg-emerald-600 text-white shadow-sm'
                    : 'text-white/40 hover:text-white/70'
                }`}
                data-testid="sa-mode-toggle-activity"
              >
                Stream Analytics
              </button>
            </div>
            {viewMode === 'activity' && (
              <span className="text-xs text-emerald-400/70 bg-emerald-500/10 px-2 py-1 rounded border border-emerald-500/20" data-testid="sa-activity-mode-badge">
                activity-based
              </span>
            )}
            {viewMode === 'activity' && data?.calibration && (
              <span
                className={`text-xs px-2 py-1 rounded border ${
                  data.calibration.correlation_score >= 0.85
                    ? 'text-green-400/80 bg-green-500/10 border-green-500/20'
                    : data.calibration.correlation_score >= 0.7
                    ? 'text-yellow-400/80 bg-yellow-500/10 border-yellow-500/20'
                    : 'text-orange-400/80 bg-orange-500/10 border-orange-500/20'
                }`}
                data-testid="sa-calibration-badge"
                title={`Smoothing: ${data.calibration.smoothing_window}, Stability: ${data.calibration.stability}`}
              >
                corr: {data.calibration.correlation_score?.toFixed(2)} | {data.calibration.stability}
              </span>
            )}
            {viewMode === 'activity' && data?.confidence && (
              <span
                className={`text-xs px-2 py-1 rounded border inline-flex items-center gap-1 ${
                  data.confidence.state === 'strong'
                    ? 'text-green-400/80 bg-green-500/10 border-green-500/20'
                    : data.confidence.state === 'good'
                    ? 'text-blue-400/80 bg-blue-500/10 border-blue-500/20'
                    : data.confidence.state === 'calibrating'
                    ? 'text-yellow-400/80 bg-yellow-500/10 border-yellow-500/20'
                    : 'text-red-400/80 bg-red-500/10 border-red-500/20'
                }`}
                data-testid="sa-confidence-badge"
                title={`Confidence: ${(data.confidence.confidence * 100).toFixed(0)}% | Quality: ${data.data_quality?.level || '—'}`}
              >
                {data.confidence.state === 'strong' ? <Shield className="w-3 h-3" />
                  : data.confidence.state === 'unreliable' ? <AlertTriangle className="w-3 h-3" />
                  : <Activity className="w-3 h-3" />}
                {(data.confidence.confidence * 100).toFixed(0)}% {data.confidence.level}
              </span>
            )}
            {viewMode === 'activity' && data?.data_quality && (
              <span
                className={`text-xs px-2 py-1 rounded border ${
                  data.data_quality.level === 'high'
                    ? 'text-green-400/60 bg-green-500/5 border-green-500/15'
                    : data.data_quality.level === 'medium'
                    ? 'text-yellow-400/60 bg-yellow-500/5 border-yellow-500/15'
                    : data.data_quality.level === 'low'
                    ? 'text-orange-400/60 bg-orange-500/5 border-orange-500/15'
                    : 'text-red-400/60 bg-red-500/5 border-red-500/15'
                }`}
                data-testid="sa-data-quality-badge"
                title={`Continuity: ${(data.data_quality.metrics?.continuity * 100).toFixed(0)}% | Coverage: ${(data.data_quality.metrics?.active_coverage * 100).toFixed(0)}%`}
              >
                quality: {data.data_quality.level}
              </span>
            )}
          </div>

          {/* Date Range Controls */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2">
              <label className="text-white/50 text-xs">Από</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="bg-white/10 border border-white/20 rounded-md px-2.5 py-1.5 text-sm text-white focus:border-blue-500 focus:outline-none"
                data-testid="sa-listener-date-from"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-white/50 text-xs">Έως</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="bg-white/10 border border-white/20 rounded-md px-2.5 py-1.5 text-sm text-white focus:border-blue-500 focus:outline-none"
                data-testid="sa-listener-date-to"
              />
            </div>
            <Button 
              size="sm" 
              variant="outline" 
              onClick={fetchReport} 
              disabled={loading} 
              className="border-white/20 text-white/70 hover:bg-white/10"
              data-testid="sa-listener-refresh"
            >
              {loading ? <Clock className="w-4 h-4 animate-spin" /> : <ArrowUpDown className="w-4 h-4" />}
            </Button>
            {data?.num_days > 1 && (
              <span className="text-xs text-white/40">
                ({data.num_days} ημέρες)
              </span>
            )}
          </div>

          {/* Daypart Filter */}
          <div className="flex gap-0.5 bg-white/5 rounded-lg p-0.5 flex-wrap" data-testid="sa-daypart-filter">
            {[
              { key: 'all', label: t('i18nB3_all_day') },
              { key: 'morning', label: t('i18nB3_morning_06_10') },
              { key: 'midday', label: t('i18nB3_midday_10_15') },
              { key: 'afternoon', label: t('i18nB3_drive_15_19') },
              { key: 'drive', label: 'Drive 18-22' },
              { key: 'night', label: t('i18nB3_night_22_06') },
            ].map(dp => (
              <button
                key={dp.key}
                data-testid={`sa-daypart-${dp.key}`}
                onClick={() => setDaypart(dp.key)}
                className={`px-2.5 py-1.5 text-xs rounded-md transition-colors whitespace-nowrap ${
                  daypart === dp.key
                    ? 'bg-blue-600 text-white font-medium'
                    : 'text-gray-400 hover:text-gray-200 hover:bg-white/5'
                }`}
              >
                {dp.label}
              </button>
            ))}
          </div>

          {/* Empty state for daypart with no data */}
          {!loading && data && !summary && daypart !== 'all' && (
            <div
              className="bg-amber-500/5 border border-amber-500/20 rounded-lg px-4 py-3 flex items-center gap-2.5 text-sm text-amber-300/90"
              data-testid="sa-daypart-empty-state"
            >
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span>Δεν υπάρχουν δεδομένα για αυτή τη ζώνη στο επιλεγμένο διάστημα</span>
            </div>
          )}

          {/* Summary Cards */}
          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="sa-listener-summary-cards">
              {[
                { label: labels.peak, value: formatNum(summary.peak), Icon: TrendingUp, color: 'bg-rose-600/30 text-rose-400' },
                { label: labels.avg, value: formatNum(summary.avg), Icon: Users, color: 'bg-blue-600/30 text-blue-400' },
                { label: labels.min, value: formatNum(summary.min), Icon: TrendingDown, color: 'bg-amber-600/30 text-amber-400' },
                { label: labels.tlh, value: formatTLH(summary.tlh), Icon: Clock, color: 'bg-emerald-600/30 text-emerald-400' },
              ].map(({ label, value, Icon, color }) => (
                <div key={label} className="bg-white/5 border border-white/10 rounded-lg p-3 flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${color}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-xs text-white/40 uppercase tracking-wide">{label}</div>
                    <div className="text-lg font-bold text-white">{value}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* v2.9.159 — Live24 Summary Strip — appears when the station
              has a Live24 stats URL configured. Shows the raw figures
              Live24 reports for the same day so the user can compare
              with our pipeline's numbers side-by-side. */}
          {hasLive24Overlay && live24?.summary && (
            <div
              className="bg-orange-500/5 border border-orange-500/20 rounded-lg p-3"
              data-testid="sa-live24-summary"
            >
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 rounded-full bg-orange-500" />
                <span className="text-xs font-semibold text-orange-300 uppercase tracking-wide">
                  RadioApps — Στατιστικά πλατφόρμας
                </span>
                <span className="text-[10px] text-orange-200/60">({live24.summary.hours_with_data} ώρες με data)</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-white/40 text-xs">Peak:</span>
                  <span className="text-rose-300 font-medium">{formatNum(live24.summary.peak)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-white/40 text-xs">Avg:</span>
                  <span className="text-orange-300 font-medium">{formatNum(live24.summary.avg)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-white/40 text-xs">Min:</span>
                  <span className="text-amber-300 font-medium">{formatNum(live24.summary.min)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-white/40 text-xs">TLH:</span>
                  <span className="text-emerald-300 font-medium">{formatTLH(live24.summary.tlh)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Charts */}
          {hasChartData && (
            <Tabs defaultValue="bar" className="w-full">
              <TabsList className="bg-white/10 border border-white/10 mb-1">
                <TabsTrigger value="bar" className="data-[state=active]:bg-white/20 text-white/70 text-xs">Bar Chart</TabsTrigger>
                <TabsTrigger value="line" className="data-[state=active]:bg-white/20 text-white/70 text-xs">Line Chart</TabsTrigger>
              </TabsList>
              <TabsContent value="bar" className="mt-4">
                <div className="bg-white/5 border border-white/10 rounded-lg p-3 pt-8">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={chartData} margin={{ top: 25, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis dataKey="name" tick={{ fill: '#9ca3af', fontSize: 11 }} interval={1} />
                      <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} />
                      <Tooltip content={<ChartTooltip />} />
                      <Legend wrapperStyle={{ fontSize: 12, color: '#d1d5db' }} formatter={legendFormatter} />
                      <Bar dataKey="Peak" fill="#f43f5e" radius={[3, 3, 0, 0]} opacity={0.85} />
                      <Bar dataKey="Avg" fill="#3b82f6" stackId="avg-stack" radius={[hasLive24Overlay ? 0 : 3, hasLive24Overlay ? 0 : 3, 0, 0]} opacity={0.85} name={t('i18nB3_our_avg')} />
                      {hasLive24Overlay && (
                        <Bar dataKey="Live24_Avg" fill="#f59e0b" stackId="avg-stack" radius={[3, 3, 0, 0]} opacity={0.85} name="RadioApps Avg" />
                      )}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </TabsContent>
              <TabsContent value="line" className="mt-4">
                <div className="bg-white/5 border border-white/10 rounded-lg p-3 pt-8">
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={chartData} margin={{ top: 25, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis dataKey="name" tick={{ fill: '#9ca3af', fontSize: 11 }} interval={1} />
                      <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} />
                      <Tooltip content={<ChartTooltip />} />
                      <Legend wrapperStyle={{ fontSize: 12, color: '#d1d5db' }} formatter={legendFormatter} />
                      <Line type="monotone" dataKey="Peak" stroke="#f43f5e" strokeWidth={2} dot={{ r: 3, fill: '#f43f5e' }} activeDot={{ r: 5 }} />
                      <Line type="monotone" dataKey="Avg" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3, fill: '#3b82f6' }} activeDot={{ r: 5 }} name={t('i18nB3_our_avg')} />
                      <Line type="monotone" dataKey="Min" stroke="#f59e0b" strokeWidth={1.5} dot={{ r: 2, fill: '#f59e0b' }} strokeDasharray="4 2" />
                      {hasLive24Overlay && (
                        <Line type="monotone" dataKey="Live24_Avg" stroke="#fb923c" strokeWidth={2} dot={{ r: 3, fill: '#fb923c' }} activeDot={{ r: 5 }} name="RadioApps Avg" />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </TabsContent>
            </Tabs>
          )}

          {/* Hourly Table */}
          {loading ? (
            <div className="space-y-2">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-8 bg-white/5 rounded animate-pulse" />
              ))}
            </div>
          ) : sortedHourly.length > 0 ? (
            <div className="overflow-x-auto rounded-lg border border-white/10" data-testid="sa-listener-table">
              <table className="w-full text-sm">
                <thead className="bg-white/5 text-white/40 border-b border-white/10">
                  <tr>
                    <SortHeader label={t('i18nB3_hour')} field="hour" />
                    <SortHeader label={labels.peak} field="peak" />
                    <SortHeader label={labels.avg} field="avg" />
                    <SortHeader label={labels.min} field="min" />
                    <SortHeader label={labels.tlh} field="tlh" />
                    <SortHeader label="Samples" field="samples" />
                  </tr>
                </thead>
                <tbody>
                  {sortedHourly.map((row, idx) => {
                    const hasData = row.samples > 0;
                    return (
                      <tr
                        key={row.hour}
                        className={`border-b border-white/5 transition-colors ${
                          hasData
                            ? idx % 2 === 0 ? 'bg-white/[0.02]' : ''
                            : 'opacity-30'
                        } ${hasData ? 'hover:bg-white/5' : ''}`}
                      >
                        <td className="px-3 py-2 font-mono text-white/70">{formatHour(row.hour)}</td>
                        <td className="px-3 py-2 text-rose-300 font-medium">{hasData ? formatNum(row.peak) : '—'}</td>
                        <td className="px-3 py-2 text-blue-300 font-medium">{hasData ? formatNum(row.avg) : '—'}</td>
                        <td className="px-3 py-2 text-amber-300">{hasData ? formatNum(row.min) : '—'}</td>
                        <td className="px-3 py-2 text-emerald-300">{hasData ? formatTLH(row.tlh) : '—'}</td>
                        <td className="px-3 py-2 text-white/30">{hasData ? row.samples : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-white/30">
              <Users className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p>Δεν υπάρχουν δεδομένα για το επιλεγμένο διάστημα</p>
            </div>
          )}

          {/* Footer */}
          <div className="text-xs text-white/30 flex flex-wrap gap-x-4 gap-y-1">
            <span>Σταθμός: {data?.station_name || stationName || '—'}</span>
            <span>Περίοδος: {data?.date_from || dateFrom} → {data?.date_to || dateTo}</span>
            {summary?.hours_with_data != null && <span>Ωρες με δεδομένα: {summary.hours_with_data}</span>}
          </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SongsAnalyzerListenerReport;
